import { LocaleService } from "../../service/locale.service";
import { DateHelper } from "../general-classes/date-helper";
import { StaticClass } from "../general-classes/static-classes";
import { GenericCounterMeasurementData } from "../generic-counter-measurements-data";
import { ComponentAdapterParent } from "./component-adapter-parent";

export class ComponentAdapterCounterParent extends ComponentAdapterParent {
  protected _measurementMap: Map<string, string> = null;
  protected _currentMonth: string = "";
  protected _previousMonth: string = "";
  protected _currentYear: string = "";
  protected _previousYear: string = "";

  /**
   * Get the map of the displayable measurements for the counter. Each item of the map is thus formed:
   * + KEY: Name of a peculiar item in the Enum "***MeasurementType"; this enum, peculiar for the specific counter "sub-type",
   *        identifies the "role"/"meaning" of a specific Measurement, as defined by its property "Kind".
   * + VALUE: The value of the specific Measurement.
   */
  get MeasurementMap(): Map<string, string> {
    return this._measurementMap;
  }

  /**
   * Get the full name of the current month, localised to the current locale
   */
  get CurrentMonth(): string {
    return this._currentMonth;
  }

  /**
   * Set externally the full name of the current month; this is useful if you want make sure that the name is dinamically
   * localised at the change of locale.
   */
  set CurrentMonth(montString: string) {
    this._currentMonth = montString;
  }

  /**
   * Get the full name of the previous month, localised to the current locale
   */
  get PreviousMonth(): string {
    return this._previousMonth;
  }

  /**
   * Set externally the full name of the previous month; this is useful if you want make sure that the name is dinamically
   * localised at the change of locale.
   */
  set PreviousMonth(montString: string) {
    this._previousMonth = montString;
  }

  /**
   * Get the full name of the previous month, localised to the current locale
   */
  get PreviousYear(): string {
    return this._previousYear;
  }

  /**
   * Get the full name of the previous month, localised to the current locale
   */
  get CurrentYear(): string {
    return this._currentYear;
  }

  /**
   * This method populates a map, where each item is thus formed:
   * + KEY: Name of a peculiar item in the Enum "***MeasurementType"; this enum, peculiar for the specific counter "sub-type",
   *        identifies the "role"/"meaning" of a specific Measurement, as defined by its property "Kind".
   * + VALUE: The value of the specific Measurement.
   *
   * It also set a fall-back value to the properties "_currentMonth", "_previousMonth", "_currentYear", "_previousYear"
   * @param listCounterMeasurement The list of current Measurements associated with the particular counter-type SmartComponent.
   * @param enumMeasurementType The SPECIFIC "enumCounterMeasurementType" enum associated with the particular counter-type SmartComponent.
   *                                ATTENTION! Please check file "enumCounterMeasurementType.ts" to make sure the correct type of Enum is
   *                                            passed to the method.
   * @param locale Locale of the names of the months, usually LocaleService.locale().
   */
  fillMeasurementMap(
    listCounterMeasurement: Array<GenericCounterMeasurementData>,
    enumMeasurementType: any,
    defaultDecimalDigits: number,
    locale: string
  ) {
    this._measurementMap = new Map<string, string>();
    for (const measurement of listCounterMeasurement) {
      const formattedValue = StaticClass.formatDecimalValue(
        measurement.value,
        defaultDecimalDigits,
        true
      );

      this._measurementMap.set(
        enumMeasurementType[measurement.kind],
        formattedValue + " " + measurement.measureUnit
      );
    }

    const now = new Date();
    this._currentMonth = LocaleService.formatDateIn(locale, now, "month").toUpperCase();
    this._previousMonth = LocaleService.formatDateIn(locale, DateHelper.subtract(now, 1, "month"), "month").toUpperCase();
    this._currentYear = DateHelper.format(now, "yyyy");
    this._previousYear = DateHelper.format(DateHelper.subtract(now, 1, "year"), "yyyy");
  }
}
